import { useEffect } from "react";
import { SITE_URL } from "utils/constants";

export const SITE_NAME = "SARimpact";
export const SITE_DESCRIPTION =
  "Damage and flood proxy maps from satellite radar, made in the first hours after a disaster and published openly by Earth Observatory of Singapore.";

// The tags this hook owns. Each is found by its selector or made once, then
// rewritten on every page, so a page never inherits the last one's values.
function headTag(selector, create) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = create();
    document.head.appendChild(el);
  }
  return el;
}

function setMeta(name, content) {
  const el = headTag(`meta[name="${name}"]`, () => {
    const meta = document.createElement("meta");
    meta.setAttribute("name", name);
    return meta;
  });
  el.setAttribute("content", content);
}

function removeTag(selector) {
  document.head.querySelector(selector)?.remove();
}

/**
 * Sets what search engines read for the page: its title, description,
 * canonical address and whether it may be indexed, plus optional structured
 * data (schema.org JSON-LD). Google runs the app's JavaScript before indexing,
 * so these count even though the HTML shell ships the site-wide values.
 *
 * `path` is the canonical path (no query); a noindex page gets no canonical.
 */
export default function usePageMeta({
  title,
  description,
  path,
  noindex = false,
  jsonLd = null,
}) {
  const ld = jsonLd ? JSON.stringify(jsonLd) : null;

  useEffect(() => {
    document.title = title ? `${title} | ${SITE_NAME}` : SITE_NAME;
    setMeta("description", description || SITE_DESCRIPTION);
    setMeta("robots", noindex ? "noindex" : "index, follow");

    if (noindex || !path) {
      removeTag('link[rel="canonical"]');
    } else {
      const link = headTag('link[rel="canonical"]', () => {
        const el = document.createElement("link");
        el.setAttribute("rel", "canonical");
        return el;
      });
      link.setAttribute("href", `${SITE_URL}${path}`);
    }

    if (ld) {
      const script = headTag('script[data-page-ld="true"]', () => {
        const el = document.createElement("script");
        el.type = "application/ld+json";
        el.dataset.pageLd = "true";
        return el;
      });
      script.textContent = ld;
    } else {
      removeTag('script[data-page-ld="true"]');
    }
  }, [title, description, path, noindex, ld]);
}
